<template>
    <div class="p-3">
        <b-row no-gutters>
            <b-col class="exit-summary formatPrice">
                <span class="text-gray-400">+8%</span><br />
                <span v-if="lowestOpenPrice">{{ formatPrice(lowestOpenPrice * simulatedCloseRatio) }}</span>
                <span v-else>N/A</span>
            </b-col>
            <b-col cols="8" class="text-right lookup-container">
                <b-button
                    @click="exitStats.splice(0, 1)"
                    variant="dark"
                    class="mr-1"
                    :disabled="!exitStats.length"
                >
                    <b-icon icon="x-circle"></b-icon>
                </b-button>

                <b-overlay
                    :show="checkExitStatsLoading"
                    opacity="0.1"
                    spinner-small
                    spinner-variant="light"
                    class="mr-1"
                >
                    <b-button 
                        @click="checkExitStats"
                        variant="dark"
                        :disabled="!targetPrice || targetPrice <= currentAsset.price" 
                    >
                        <b-icon icon="file-earmark-bar-graph"></b-icon>
                    </b-button>
                </b-overlay>

                <b-form-input
                    type="number"
                    step="any" 
                    name="balance" 
                    id="balance" 
                    placeholder="price"
                    v-model="targetPrice"
                ></b-form-input>

                <b-overlay
                    :show="formLoadingState"
                    opacity="0.1"
                    spinner-small
                    spinner-variant="light"
                >
                    <b-button 
                        @click="onSubmit"
                        :disabled="!targetPrice || targetPrice <= 0"
                        variant="dark"
                    >
                        <b-icon icon="search"></b-icon>
                    </b-button>
                </b-overlay>
            </b-col>
        </b-row>

        <b-row v-for="exitSimulation, index in exitSimulations" :key="index" no-gutters class="mt-4">
            <b-col>
                ${{ formatPrice(exitSimulation.holdingsValue, true) }} @ ${{ formatPrice(exitSimulation.targetPrice) }} (+{{ (exitSimulation.targetPrice / currentAsset.price * 100 -100).toFixed(1) }}%)<br />
                Hld: {{ formatHoldings(exitSimulation.holdings) }} ({{ formatHoldings(exitSimulation.holdings - currentHoldings) }})
            </b-col>
            <b-col>
                Exits: ${{ formatPrice(exitSimulation.totalExitValue, true) }}<br />
                Locked: ${{ formatPrice(exitSimulation.lockedValue, true) }}
            </b-col>
        </b-row>
    </div>
</template>

<script>

import apiMixin from '@/mixins/api';
import formatterMixin from '@/mixins/formatter';

import ProcessExit from '@/components/overlays/ProcessExit.vue';

import { SIMULATED_CLOSE_RATIO } from '@/constants/trading';
import { SHEET_STYLE } from '@/constants/ui';

export default {
    props: ['currentAsset'],
    name: 'AssetExitsAtTarget',
    mixins: [
        apiMixin,
        formatterMixin
    ],

    data() {
        return {
            targetPrice: null,
            formLoadingState: false,

            exitStats: []
        }
    },
    methods: {
        onSubmit() {
            if (!this.targetPrice || this.targetPrice <= 0) {
                return;
            }
        
            this.$FModal.show({ 
                component: ProcessExit, 
                ...SHEET_STYLE
            }, 
            { 
                currentAsset: this.currentAsset,
                targetPrice: this.targetPrice
            });
        },
        async checkExitStats() {
            if (!this.targetPrice || this.targetPrice <= 0) {
                return;
            }

            const response = await this.apiGet(
                `/api/asset/${this.currentAsset.symbol}/check_exits/${this.targetPrice}`, 
                { simulation: 'included' },
                { requestId: 'checkExitStats' }
            );

            if (!response?.data) return;

            response.data.target_price = this.targetPrice;

            // consider unpacking response.data.exit_simulation_results instead
            // if no other data is needed at response.data level
            this.exitStats.unshift({ ...response.data, target_price: this.targetPrice });
        }
    },
    computed: {
        simulatedCloseRatio() {
            return SIMULATED_CLOSE_RATIO;
        },
        lowestOpenPrice() {
            return this.currentAsset.cached_stats?.lowest_open_position?.open_price;
        },
        checkExitStatsLoading() {
            return !!this.apiLoading['checkExitStats'];
        },
        exitSimulations() {
            return this.exitStats.map((exitStats) => {
                const result = exitStats.exit_simulation_results;
                const totalHoldings = this.currentHoldings - result.locked_shares;
                const bonusPositionsValue = this.currentAsset.cached_stats?.sum_bonus_exit_value || 0;

                return {
                    holdings: this.formatNumber(totalHoldings),
                    targetPrice: this.formatNumber(exitStats.target_price),
                    holdingsValue: this.formatNumber(totalHoldings * exitStats.target_price),
                    lockedValue: this.formatNumber(result.remaining_value_locked - bonusPositionsValue),
                    totalExitValue: this.formatNumber(result.exit_value || 0),
                }
            });
        },
        currentHoldings() {
            return this.sumHoldings(this.currentAsset.balances || [])
        }
    }
}
</script>

<style lang="scss" scoped>
$gray-400: #ced4da !default;

.lookup-container {
    display: flex;
}

.exit-summary { 
    font-size: 0.85rem;
    height: calc(1.5em + 0.75rem + 2px);
    vertical-align: middle;
}

.text-gray-400 {
    color: $gray-400;
}

</style>